import { animate, state, style, transition, trigger } from '@angular/animations';

import { Component, inject, OnChanges, OnInit, input } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatOption } from '@angular/material/core';
import { MatSelect } from '@angular/material/select';
import { RouterLink } from '@angular/router';
import { COUNTRIES } from '../../../../shared-lazy/components/join-form/countries';
import { DotsLoaderComponent } from '../../../../shared/components/dots-loader/dots-loader.component';
import { ClickCursorDirective } from '../../../../shared/directives/click-cursor.directive';
import { AUTH_USER_ROLES, UserDetails } from '../../../auth0/models';
import { Auth0Store } from '../../../auth0/store';
import { UserUtils } from '../../../auth0/utils/user.utils';

@Component({
  selector: 'gnx-profile-details',
  templateUrl: './profile-details.component.html',
  styleUrls: ['./profile-details.component.scss'],
  animations: [
    trigger('grow', [
      state('inOut', style({ flex: 1000 })),
      transition(':leave', animate(500, style({ flex: 0 }))),
      transition(':enter', [style({ flex: 0 }), animate(500)]),
    ]),
  ],
  imports: [ReactiveFormsModule, MatSelect, MatOption, RouterLink, ClickCursorDirective, DotsLoaderComponent],
})
export class ProfileDetailsComponent implements OnChanges, OnInit {
  private fb = inject(FormBuilder);

  protected auth0Store = inject(Auth0Store);
  ROLES = AUTH_USER_ROLES;
  COUNTRIES = COUNTRIES;

  readonly data = input<UserDetails>(undefined);

  profileForm: FormGroup;
  formChanged = false;
  organizationalRole: string;
  showUserRole: boolean;

  ngOnChanges() {
    const existingCountry = this.data().country || '';
    const countryIsKnown = this.COUNTRIES.includes(existingCountry) && existingCountry !== 'Other';

    this.organizationalRole = UserUtils.isUserAnAdmin(this.data()) ? 'Admin' : 'Member';
    this.showUserRole = this.data().org_config?.userConfig?.rolesAndPermissionsConfig?.active ?? false;

    const data = this.data();
    const dataValue = this.data();
    this.profileForm = this.fb.group({
      firstName: [this.data().given_name || '', [Validators.required, this.notEmailValidator.bind(this)]],
      lastName: [this.data().family_name || '', [Validators.required, this.notEmailValidator.bind(this)]],
      email: [{ value: this.data().email, disabled: true }],
      country: [
        countryIsKnown ? existingCountry : existingCountry ? 'Other' : '',
        [Validators.required, this.countryValidator.bind(this)],
      ],
      countryOther: [countryIsKnown ? '' : existingCountry || '', [this.otherCountryValidator.bind(this)]],
      role: [this.ROLES.includes(data.roles[0]) ? data.roles[0] : '', [Validators.required]],
      organizationalRole: [{ value: this.organizationalRole, disabled: true }],
      userRole: [
        {
          value: dataValue.applicative_role
            ? dataValue.applicative_role[0].toUpperCase() + dataValue.applicative_role.slice(1)
            : '',
          disabled: true,
        },
      ],
    });

    this.profileForm.valueChanges.subscribe(() => (this.formChanged = true));
  }

  ngOnInit() {}

  onCancel() {
    const existingCountry = this.data().country || '';
    const countryIsKnown = this.COUNTRIES.includes(existingCountry) && existingCountry !== 'Other';

    const data = this.data();
    this.profileForm.controls.firstName.setValue(data.given_name || '');
    this.profileForm.controls.lastName.setValue(data.family_name || '');
    this.profileForm.controls.country.setValue(countryIsKnown ? existingCountry : existingCountry ? 'Other' : '');
    this.profileForm.controls.countryOther.setValue(countryIsKnown ? '' : existingCountry || '');
    this.profileForm.controls.role.setValue(this.ROLES.includes(data.roles[0]) ? data.roles[0] : '');
    this.formChanged = false;
  }

  onSubmit() {
    const country =
      this.profileForm.get('country').value === 'Other'
        ? this.profileForm.get('countryOther').value
        : this.profileForm.get('country').value;
    this.auth0Store.updateUserInfo({
      firstName: this.profileForm.get('firstName').value,
      lastName: this.profileForm.get('lastName').value,
      role: this.profileForm.get('role').value,
      country,
    });
  }

  get p() {
    return this.profileForm.controls;
  }

  private countryValidator(control: FormControl) {
    if (!this.profileForm) {
      return;
    }
    const country = control.value;
    if (country !== 'Other') {
      this.p.countryOther.setErrors(null);
    } else if (!this.p.countryOther.value) {
      this.p.countryOther.setErrors(
        country !== 'Other'
          ? null
          : {
              required: true,
            },
      );
    }
    return null;
  }

  private otherCountryValidator(control: FormControl) {
    const value = control.value;
    if (!value && this.profileForm && this.p.country.value === 'Other') {
      return {
        required: true,
      };
    }
    return null;
  }

  private notEmailValidator(control: FormControl) {
    const email = control.value.trim().toLowerCase();
    if (email.includes('@')) {
      return {
        email: true,
      };
    }
    return null;
  }
}
